import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Table, Badge, InputGroup, Spinner } from 'react-bootstrap';
import { FaShareAlt, FaPlus, FaLink, FaCopy, FaTrash, FaSearch, FaCar, FaClock, FaCheck, FaSync } from 'react-icons/fa';
import { GetFromApi, PostToApi, DeleteFromApi } from '../utils/api';
import '../styles/Pages.css';

const ShareVehicle = () => {
  const [showModal, setShowModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [generatedLink, setGeneratedLink] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shares, setShares] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [formData, setFormData] = useState({ vehicle_id: '', shared_with: '', email: '', expiry_days: 7, allow_live_track: true, allow_history: false, allow_reports: false });

  const fetchShares = async () => {
    try {
      setLoading(true);
      const response = await GetFromApi('/shares');
      if (response.success) setShares(response.data);
    } catch (error) {
      console.error('Error fetching shares:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await GetFromApi('/vehicles');
      if (response.success) setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  useEffect(() => { fetchShares(); fetchVehicles(); }, []);

  const filteredShares = shares.filter(share => {
    const matchSearch = share.vehicle_number?.toLowerCase().includes(searchTerm.toLowerCase()) || share.shared_with?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = filterStatus === 'all' || share.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const handleOpenModal = () => {
    setFormData({ vehicle_id: '', shared_with: '', email: '', expiry_days: 7, allow_live_track: true, allow_history: false, allow_reports: false });
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await PostToApi('/shares', formData);
      if (response.success) {
        setGeneratedLink(response.data.link);
        handleCloseModal();
        setShowLinkModal(true);
        await fetchShares();
      }
    } catch (error) {
      console.error('Error creating share:', error);
      alert('Error creating share: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (id) => {
    if (window.confirm('Are you sure you want to revoke this share access?')) {
      try {
        await DeleteFromApi(`/shares/${id}`);
        await fetchShares();
      } catch (error) {
        console.error('Error revoking share:', error);
      }
    }
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const activeCount = shares.filter(s => s.status === 'active').length;
  const expiredCount = shares.filter(s => s.status === 'expired').length;

  return (
    <Container fluid className="p-3">
      <div className="page-header">
        <h4 className="page-title">Share Vehicle</h4>
        <div className="d-flex gap-2">
          <Button variant="light" onClick={fetchShares} disabled={loading} style={{ borderRadius: '8px' }}>{loading ? <Spinner size="sm" /> : <FaSync />}</Button>
          <Button variant="primary" onClick={handleOpenModal} style={{ borderRadius: '8px' }}><FaPlus className="me-2" /> Share Vehicle</Button>
        </div>
      </div>

      <Row className="mb-4">
        <Col md={4} sm={6} className="mb-3">
          <Card className="stats-card border-0 shadow-sm">
            <div className="d-flex align-items-center">
              <div className="icon-box me-3" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3B82F6' }}><FaShareAlt /></div>
              <div><div className="stats-value">{shares.length}</div><div className="stats-label">Total Shares</div></div>
            </div>
          </Card>
        </Col>
        <Col md={4} sm={6} className="mb-3">
          <Card className={`stats-card border-0 shadow-sm ${filterStatus === 'active' ? 'border border-2 border-success' : ''}`} style={{ cursor: 'pointer' }} onClick={() => setFilterStatus(filterStatus === 'active' ? 'all' : 'active')}>
            <div className="d-flex align-items-center">
              <div className="icon-box me-3" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#22C55E' }}><FaLink /></div>
              <div><div className="stats-value">{activeCount}</div><div className="stats-label">Active Shares</div></div>
            </div>
          </Card>
        </Col>
        <Col md={4} sm={6} className="mb-3">
          <Card className={`stats-card border-0 shadow-sm ${filterStatus === 'expired' ? 'border border-2 border-danger' : ''}`} style={{ cursor: 'pointer' }} onClick={() => setFilterStatus(filterStatus === 'expired' ? 'all' : 'expired')}>
            <div className="d-flex align-items-center">
              <div className="icon-box me-3" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#EF4444' }}><FaClock /></div>
              <div><div className="stats-value">{expiredCount}</div><div className="stats-label">Expired Shares</div></div>
            </div>
          </Card>
        </Col>
      </Row>

      <Card className="page-card">
        <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
          <h6 className="mb-0 fw-semibold text-dark">Shared Vehicles</h6>
          <div className="search-input-wrapper">
            <FaSearch className="search-icon" />
            <Form.Control type="text" placeholder="Search shares..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '2.5rem', borderRadius: '8px', width: '250px' }} />
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead style={{ backgroundColor: '#f8fafc' }}><tr><th>Vehicle</th><th>Shared With</th><th>Share Date</th><th>Expiry Date</th><th>Permissions</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {filteredShares.map(share => (
                  <tr key={share.id}>
                    <td><div className="d-flex align-items-center"><div className="me-2 d-flex align-items-center justify-content-center" style={{ width: 36, height: 36, borderRadius: 8, backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3B82F6' }}><FaCar /></div><span className="fw-semibold">{share.vehicle_number}</span></div></td>
                    <td><div className="fw-medium">{share.shared_with}</div><small className="text-muted">{share.email}</small></td>
                    <td>{share.shared_date && new Date(share.shared_date).toLocaleDateString()}</td>
                    <td>{share.expiry_date && new Date(share.expiry_date).toLocaleDateString()}</td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {share.permissions?.allowLiveTrack && <Badge bg="light" text="dark" className="small">Live Track</Badge>}
                        {share.permissions?.allowHistory && <Badge bg="light" text="dark" className="small">History</Badge>}
                        {share.permissions?.allowReports && <Badge bg="light" text="dark" className="small">Reports</Badge>}
                      </div>
                    </td>
                    <td><Badge bg={share.status === 'active' ? 'success' : 'secondary'} className="rounded-pill">{share.status === 'active' ? 'Active' : 'Expired'}</Badge></td>
                    <td>
                      <div className="d-flex gap-1">
                        <button className="action-btn view" onClick={() => handleCopyLink(share.link)} title="Copy Link"><FaCopy size={14} /></button>
                        {share.status === 'active' && <button className="action-btn delete" onClick={() => handleRevoke(share.id)} title="Revoke"><FaTrash size={14} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {filteredShares.length === 0 && !loading && (
            <div className="empty-state py-5"><div className="empty-state-icon"><FaShareAlt /></div><div className="empty-state-title">No shares found</div></div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal} centered className="modal-custom">
        <Modal.Header closeButton><Modal.Title>Share Vehicle</Modal.Title></Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3"><Form.Label className="form-label-custom">Select Vehicle</Form.Label>
              <Form.Select className="form-control-custom" value={formData.vehicle_id} onChange={(e) => setFormData({...formData, vehicle_id: e.target.value})} required>
                <option value="">Choose a vehicle</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.number}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3"><Form.Label className="form-label-custom">Share With (Name)</Form.Label><Form.Control type="text" className="form-control-custom" value={formData.shared_with} onChange={(e) => setFormData({...formData, shared_with: e.target.value})} required /></Form.Group>
            <Form.Group className="mb-3"><Form.Label className="form-label-custom">Email Address</Form.Label><Form.Control type="email" className="form-control-custom" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required /></Form.Group>
            <Form.Group className="mb-3"><Form.Label className="form-label-custom">Expires In</Form.Label>
              <Form.Select className="form-control-custom" value={formData.expiry_days} onChange={(e) => setFormData({...formData, expiry_days: parseInt(e.target.value)})}>
                <option value={1}>1 Day</option><option value={3}>3 Days</option><option value={7}>7 Days</option><option value={14}>14 Days</option><option value={30}>30 Days</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3"><Form.Label className="form-label-custom">Permissions</Form.Label>
              <div className="p-3 rounded" style={{ backgroundColor: '#f8fafc' }}>
                <Form.Check type="checkbox" label="Allow Live Tracking" checked={formData.allow_live_track} onChange={(e) => setFormData({...formData, allow_live_track: e.target.checked})} className="mb-2" />
                <Form.Check type="checkbox" label="Allow History View" checked={formData.allow_history} onChange={(e) => setFormData({...formData, allow_history: e.target.checked})} className="mb-2" />
                <Form.Check type="checkbox" label="Allow Reports Access" checked={formData.allow_reports} onChange={(e) => setFormData({...formData, allow_reports: e.target.checked})} />
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={handleCloseModal}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={saving}>{saving ? <Spinner size="sm" className="me-2" /> : <FaLink className="me-2" />} Generate Link</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showLinkModal} onHide={() => setShowLinkModal(false)} centered className="modal-custom">
        <Modal.Header closeButton><Modal.Title>Share Link Generated</Modal.Title></Modal.Header>
        <Modal.Body className="text-center">
          <div className="mx-auto mb-4 d-flex align-items-center justify-content-center" style={{ width: 80, height: 80, borderRadius: '50%', backgroundColor: 'rgba(34, 197, 94, 0.1)' }}><FaCheck size={40} color="#22C55E" /></div>
          <h5 className="mb-3">Share link has been created!</h5>
          <p className="text-muted mb-4">Copy the link below and share it with the recipient.</p>
          <InputGroup className="mb-3">
            <Form.Control type="text" value={generatedLink} readOnly style={{ backgroundColor: '#f8fafc' }} />
            <Button variant={copiedLink ? 'success' : 'primary'} onClick={() => handleCopyLink(generatedLink)}>{copiedLink ? <><FaCheck className="me-1" /> Copied</> : <><FaCopy className="me-1" /> Copy</>}</Button>
          </InputGroup>
        </Modal.Body>
        <Modal.Footer className="justify-content-center"><Button variant="light" onClick={() => setShowLinkModal(false)}>Close</Button></Modal.Footer>
      </Modal>

      {copiedLink && <div className="position-fixed bottom-0 end-0 p-3" style={{ zIndex: 9999 }}><div className="bg-dark text-white px-4 py-2 rounded shadow"><FaCheck className="me-2" /> Link copied!</div></div>}
    </Container>
  );
};

export default ShareVehicle;
